import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { CampaignService } from '@core/services/campaign.service';
import * as CampaignActions from './campaign.actions';
import { selectCampaignsLoaded } from './campaign.selectors';

@Injectable()
export class CampaignEffects {
    private actions$ = inject(Actions);
    private translate = inject(TranslateService);
    private campaignService = inject(CampaignService);
    private store = inject(Store);

    loadCampaigns$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignActions.loadCampaigns),
            withLatestFrom(this.store.select(selectCampaignsLoaded)),
            filter(([, loaded]) => !loaded),
            mergeMap(() =>
                this.campaignService.loadCampaigns().pipe(
                    map((pages) => CampaignActions.loadCampaignsSuccess({ pages })),
                    catchError((error) =>
                        of(
                            CampaignActions.loadCampaignsFailure({
                                error: error.message || this.translate.instant('storeErrors.loadCampaigns'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignActions.loadCampaign),
            mergeMap(({ id }) =>
                this.campaignService.getCampaignById(id).pipe(
                    map((campaign) => {
                        if (campaign) {
                            return CampaignActions.loadCampaignSuccess({
                                campaign,
                            });
                        } else {
                            throw new Error(this.translate.instant('storeErrors.campaignNotFound'));
                        }
                    }),
                    catchError((error) =>
                        of(
                            CampaignActions.loadCampaignFailure({
                                error: error.message || this.translate.instant('storeErrors.loadCampaign'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
